import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import deepEqual from 'fast-deep-equal';
import pick from 'lodash-es/pick';

import { safeEmptyArray } from '@kc-console/core';

import {
  getFilterDefMap,
  isCollectionFilterDef,
  isSelectFilterOptionDef,
} from '../../components/DataTable/utils';
import {
  convertFilterToQueryState,
  convertQueryToFilterState,
  pruneInvalidFilterState,
} from '../../utils/searchCondition/filterStateConverter';
import {
  convertPaginationToQueryState,
  convertQueryToPaginationState,
} from '../../utils/searchCondition/paginationStateConverter';
import {
  convertQueryToSortState,
  convertSortToQueryState,
} from '../../utils/searchCondition/sortStateConverter';
import useQueryState from '../useQueryState';
import useAutoResetHandlers from './useAutoResetHandlers';
import useFilterState from './useFilterState';
import usePaginationState from './usePaginationState';
import useSortState from './useSortState';
import useTableSettings from './useTableSettings';

import type { FilterDefBase, FilterState, SortState } from '../../components/DataTable/types';
import type {
  FilterCondition,
  PaginationState,
  QuerySyncOptions,
  SearchCondition,
  SortCondition,
} from './types';
import type { AutoResetOptions } from './useAutoResetHandlers';
import type { ScreenTableSetting } from './useTableSettings';

interface InitialState {
  filter?: FilterState[];
  page?: PaginationState;
  sort?: SortState[];
  tableSettings?: Partial<ScreenTableSetting>;
}

interface Props {
  filters: FilterDefBase[];
  /**
   *
   */
  autoReset?: AutoResetOptions;
  /**
   * querystring 동기화 설정
   * - boolean: 모든 상태(page, filter, sort)에 일괄 적용
   * - QuerySyncOptions: 각 상태별로 개별 설정
   *
   * @default false
   *
   * @example
   * // 모든 상태 동기화
   * enableQuerySync: true
   *
   * // page만 querystring 무시 (첫 렌더링 시 항상 page=1)
   * enableQuerySync: { page: false, filter: true, sort: true }
   */
  enableQuerySync?: boolean | QuerySyncOptions;
  initialState?: InitialState;
  /**
   * 클라이언트에서 정렬하는 경우 `executeDataSearch`에서 사용하는 커스텀 정렬 함수를 정의합니다.
   *
   * `direction: "asc"`인 경우에 정렬 하는 함수를 정의하세요, `"desc"`인 경우에는 역순으로 적용됩니다.
   *
   * 전역 상수 또는 memo된 값을 전달하세요.
   */
  sortFns?: Record<string, (aValue: any, bValue: any, aData: any, bData: any) => number>;
  /**
   * table setting을 사용하지 않으면 사용하지 않음
   */
  tableKey?: string;
}

const defaultFilterState: FilterState[] = [];
const defaultSortState: SortState[] = [];

export default function useTableSearchCondition({
  tableKey,
  enableQuerySync = false,
  filters,
  initialState,
  sortFns,
  autoReset,
}: Props) {
  const router = useRouter();

  const {
    filter: initialFilterState = defaultFilterState,
    page: initialPageState,
    sort: initialSortState = defaultSortState,
    tableSettings: initialTableState,
  } = initialState ?? {};

  const [storedTableSettings, setStoredTableSettings] = useTableSettings(
    tableKey,
    initialTableState,
  );

  /**
   * enableQuerySync 정규화
   * @description
   * - boolean이면 모든 옵션(page, filter, sort)에 동일하게 적용
   * - QuerySyncOptions 객체면 각 옵션별로 개별 적용
   * - 하위 호환성 유지: true/false는 기존과 동일하게 동작
   */
  const querySyncOptions = useMemo<Required<QuerySyncOptions>>(() => {
    if (typeof enableQuerySync === 'boolean') {
      return {
        page: enableQuerySync,
        filter: enableQuerySync,
        sort: enableQuerySync,
      };
    }
    return {
      page: enableQuerySync.page ?? false,
      filter: enableQuerySync.filter ?? false,
      sort: enableQuerySync.sort ?? false,
    };
  }, [enableQuerySync]);

  const safeFilters = useMemo(() => safeEmptyArray(filters), [filters]);
  const filterDefMap = useMemo(() => getFilterDefMap(safeFilters), [safeFilters]);

  /**
   * 필터 옵션 비동기 로딩이 완료되었는지 여부.
   * @description 최상위 및 property 내부 select/collection 필터 중 isOptionsLoading=true가 하나라도 있으면 false.
   */
  const isOptionsLoaded = useMemo(
    () =>
      !Object.values(filterDefMap).some(
        filterDef =>
          (isSelectFilterOptionDef(filterDef) || isCollectionFilterDef(filterDef)) &&
          filterDef.isOptionsLoading === true,
      ),
    [filterDefMap],
  );

  const initialPageIndex = initialPageState?.pageIndex ?? 1;
  const initialPageSize = storedTableSettings.pageSize ?? initialPageState?.pageSize ?? 20;

  /**
   * queryState - 하나라도 sync가 활성화되어 있으면 useQueryState 활성화
   */
  const isQuerySyncEnabled =
    querySyncOptions.page || querySyncOptions.filter || querySyncOptions.sort;
  const [queryState, updateQueryState] = useQueryState({ enableQuerySync: isQuerySyncEnabled });

  /**
   * 각 상태는 개별 querySyncOptions에 따라 querystring 동기화 여부 결정
   */
  const [filterState, setFilterState] = useFilterState(() => {
    return querySyncOptions.filter
      ? convertQueryToFilterState(queryState, initialFilterState, filterDefMap)
      : initialFilterState;
  });

  /**
   * filterState가 한 번이라도 변경되었는지 여부.
   */
  const [hasPrunedOnce, setHasPrunedOnce] = useState(() => !querySyncOptions.filter);

  useEffect(() => {
    if (!querySyncOptions.filter) {
      return;
    }
    if (!isOptionsLoaded) {
      return;
    }
    setFilterState(prev => pruneInvalidFilterState(prev, filterDefMap));
    setHasPrunedOnce(true);
  }, [filterDefMap, isOptionsLoaded, querySyncOptions.filter, setFilterState]);

  /**
   * 필터가 호출 측 API 요청에 사용 가능한 상태인지 여부.
   * @description
   * - `isOptionsLoaded`: 비동기 옵션을 사용하는 필터(`isOptionsLoading`)가 모두 로딩 완료됨
   * - `hasPrunedOnce`: querystring으로 복원한 필터 값에서 무효값 정리가 1회 이상 수행됨
   *
   * 옵션을 API로 받아오는 필터가 있다면 목록 조회 쿼리의 `enabled`에 이 값을 전달해,
   * 옵션 로딩과 필터 값 정리가 끝난 뒤 요청이 나가도록 하세요.
   */
  const isFilterReady = isOptionsLoaded && hasPrunedOnce;

  const [pageState, setPageState] = usePaginationState(() =>
    querySyncOptions.page
      ? convertQueryToPaginationState(queryState, initialPageIndex, initialPageSize)
      : { pageIndex: initialPageIndex, pageSize: initialPageSize },
  );

  const [sortState, setSortState] = useSortState(() =>
    querySyncOptions.sort
      ? convertQueryToSortState(queryState, initialSortState)
      : initialSortState,
  );

  /**
   * 현재 queryState 기준의 state
   * 비교할 필요가 없는 키는 제외하고 생성해준다.
   */
  const currentQueryState = useMemo(() => {
    const queryKeys = ['page', ...Object.keys(filterDefMap), 'sort'];

    return pick(queryState, queryKeys);
  }, [filterDefMap, queryState]);
  /**
   * 현재 filter/pagination/sort 기준의 state
   * @description
   * - querySyncOptions에 따라 조건부로 포함
   * - false인 옵션은 URL에 반영하지 않음
   */
  const nextQueryState = useMemo(() => {
    return {
      ...(querySyncOptions.filter ? convertFilterToQueryState(filterState, filterDefMap) : {}),
      ...(querySyncOptions.page ? convertPaginationToQueryState(pageState) : {}),
      ...(querySyncOptions.sort ? convertSortToQueryState(sortState) : {}),
    };
  }, [querySyncOptions, filterDefMap, filterState, pageState, sortState]);

  const { resetPageIndexIfNeeded, resetSortIfNeeded } = useAutoResetHandlers({
    autoReset,
    context: { setPageState, setSortState, initialState },
  });

  const updateFilterState = useCallback(
    (nextState: FilterState[]) => {
      setFilterState(nextState);
      resetPageIndexIfNeeded('filter');
      resetSortIfNeeded('filter');
    },
    [setFilterState, resetPageIndexIfNeeded, resetSortIfNeeded],
  );
  const updatePaginationState = useCallback(
    (nextState: Partial<PaginationState>) => {
      if (nextState) {
        setPageState(prev => ({
          ...prev,
          ...nextState,
        }));
      }
    },
    [setPageState],
  );
  const updateSortState = useCallback(
    (nextState: SortState[]) => {
      setSortState(nextState);
      resetPageIndexIfNeeded('sort');
    },
    [setSortState, resetPageIndexIfNeeded],
  );

  const handlePageIndexChange = useCallback(
    (pageIndex: number) => {
      updatePaginationState({ pageIndex });
    },
    [updatePaginationState],
  );
  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      updatePaginationState({ pageSize });
      setStoredTableSettings({ pageSize });
      resetPageIndexIfNeeded('pageSize');
    },
    [setStoredTableSettings, updatePaginationState, resetPageIndexIfNeeded],
  );
  const handleTableSettingChange = useCallback(
    (value: Partial<ScreenTableSetting>) => {
      if (value) {
        setStoredTableSettings(value);
      }
    },
    [setStoredTableSettings],
  );

  const searchCondition: SearchCondition = useMemo(() => {
    const filterCondition = filterState.reduce<Record<string, FilterCondition>>((acc, item) => {
      const itemDef = filterDefMap[item.key];

      if (!itemDef) {
        return acc;
      }

      if (!acc[item.key]) {
        acc[item.key] = {
          id: item.key,
          type: itemDef.type,
          selector: itemDef.selector,
          comparator: itemDef.comparator,
          enableMultiSelect: itemDef.enableMultiSelect,
          values: [],
        };
      }

      acc[item.key].values.push(item.value);

      return acc;
    }, {});

    const sortCondition = sortState.reduce<SortCondition[]>((acc, item) => {
      acc.push({
        id: item.key,
        direction: item.direction,
        sortFn: sortFns?.[item.key],
      });

      return acc;
    }, []);

    return {
      filter: Object.values(filterCondition),
      pagination: pageState,
      sort: sortCondition,
    };
  }, [filterDefMap, filterState, pageState, sortFns, sortState]);

  useEffect(() => {
    // router가 준비되지 않았으면 동기화하지 않음 (CSR에서 무한 루프 방지)
    if (!router.isReady) {
      return;
    }

    // queryState가 변경되었을 때만 updateQueryState를 호출한다.
    if (isQuerySyncEnabled && !deepEqual(currentQueryState, nextQueryState)) {
      updateQueryState(nextQueryState);
    }
  }, [currentQueryState, nextQueryState, isQuerySyncEnabled, router.isReady, updateQueryState]);

  return {
    updateQueryState,
    filterState,
    setFilterState: updateFilterState,
    pageIndex: pageState.pageIndex,
    pageSize: pageState.pageSize,
    setPageIndex: handlePageIndexChange,
    setPageSize: handlePageSizeChange,
    sortState,
    setSortState: updateSortState,
    tableSettings: storedTableSettings,
    setTableSettings: handleTableSettingChange,
    initialTableState,
    searchCondition,
    isFilterReady,
  };
}
